import React, { useState, useRef, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import './Pages/PagesCss/NavBar.css';

const NavBar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [mobileSubMenu, setMobileSubMenu] = useState(null);
  const navRef = useRef(null);
  const location = useLocation();

  const toggleDropdown = (index) => {
    setDropdownOpen(dropdownOpen === index ? null : index);
  };

  const toggleMobile = () => {
    setMobileOpen(!mobileOpen);
    if (!mobileOpen) {
      setDropdownOpen(null);
      setMobileSubMenu(null);
    }
  };

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setDropdownOpen(null);
    setMobileSubMenu(null);
  };

  const openMobileSubMenu = (index) => setMobileSubMenu(index);
  const closeMobileSubMenu = () => setMobileSubMenu(null);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    // Prevent body scroll when mobile menu is open
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [mobileOpen]);

  const handleKeyDown = (event, index) => {
    if (event.key === 'Escape') {
      setDropdownOpen(null);
      setMobileOpen(false);
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleDropdown(index);
    }
  };

  const handleMobileKeyDown = (event, index) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openMobileSubMenu(index);
    } else if (event.key === 'Escape') {
      closeMobileSubMenu();
    }
  };

  const menuItems = [
    { name: "Home", path: "/" },
    {
  name: "Basic Math",
  dropdown: [
    {
      title: "Arithmetic",
      links: [
        { name: "Addition Calculator", path: "/AdditionCalculator" },
        { name: "Subtraction Calculator", path: "/SubtractionCalculator" },
        { name: "Multiplication Calculator", path: "/MultiplicationCalculator" },
        { name: "Division Calculator", path: "/DivisionCalculator" },
        { name: "Percentage Calculator", path: "/PercentageCalculator" },
        { name: "Rounding Calculator", path: "/RoundingCalculator" },
        { name: "Average Calculator", path: "/AverageCalculator" },
        { name: "Sum of Series Calculator", path: "/SumOfSeriesCalculator" },
    { name: "Integer Calculator", path: "/IntegerCalculator" },
    { name: "Number Comparator", path: "/NumberComparator" }
      ]
    },

    {
      title: "Numbers",
      links: [
        { name: "Fraction Simplifier", path: "/FractionSimplifier" },
        { name: "Mixed Fraction Calculator", path: "/MixedFractionCalculator" },
        { name: "Ratio Calculator", path: "/RatioCalculator" },
        { name: "Proportion Calculator", path: "/ProportionCalculator" },
        { name: "Number Sequence Generator", path: "/NumberSequenceGenerator" },
        { name: "LCM Calculator", path: "/LCMCalculator" },
        { name: "HCF / GCD Calculator", path: "/HCFCalculator" },
        { name: "Prime Number Checker", path: "/PrimeNumberChecker" },
        { name: "Prime Factorization", path: "/PrimeFactorization" },
        { name: "Even or Odd Checker", path: "/EvenOddChecker" }
      ]
    },

    {
      title: "Advanced",
      links: [
        { name: "Exponents Calculator", path: "/ExponentsCalculator" },
        { name: "Root Calculator", path: "/RootCalculator" },
        { name: "Scientific Notation Converter", path: "/ScientificNotationConverter" },
        { name: "Significant Figures Calculator", path: "/SignificantFiguresCalculator" },
        { name: "Absolute Value Calculator", path: "/AbsoluteValueCalculator" },
        { name: "Random Number Generator", path: "/RandomNumberGenerator" },
        { name: "Cube Root Calculator", path: "/CubeRootCalculator" },
    { name: "Nth Root Calculator", path: "/NthRootCalculator" },
    { name: "Antilog Calculator", path: "/AntilogCalculator" },
    { name: "Radical Simplifier", path: "/RadicalSimplifier" }
      ]
    }
  ]
},
    {
  name: "Geometry",
  dropdown: [
    {
      title: "2D Shapes",
      links: [
        { name: "Area of Circle", path: "/AreaCircle" },
        { name: "Area of Triangle", path: "/AreaTriangle" },
        { name: "Area of Square", path: "/AreaSquare" },
        { name: "Area of Rectangle", path: "/AreaRectangle" },
        { name: "Area of Parallelogram", path: "/AreaParallelogram" },
        { name: "Area of Trapezium", path: "/AreaTrapezium" },
        { name: "Area of Rhombus", path: "/AreaRhombus" },
        { name: "Area of Ellipse", path: "/AreaEllipse" },
        { name: "Perimeter of Circle", path: "/PerimeterCircle" },
        { name: "Perimeter of Triangle", path: "/PerimeterTriangle" },
        { name: "Perimeter of Polygon", path: "/PerimeterPolygon" },
        { name: "Coordinate Distance Calculator", path: "/CoordinateDistance" },
        { name: "Midpoint Calculator", path: "/MidpointCalculator" },
        { name: "Slope Calculator", path: "/SlopeCalculator" },
        { name: "Angle Calculator", path: "/AngleCalculator" },
        { name: "Triangle Side Solver (SSS, SAS, ASA, RHS)", path: "/TriangleSideSolver" },
        { name: "Heron’s Formula Calculator", path: "/HeronsFormulaCalculator" },
        { name: "Triangle Height Calculator", path: "/TriangleHeight" },
        { name: "Circle Sector Area", path: "/CircleSectorArea" },
        { name: "Arc Length Calculator", path: "/ArcLengthCalculator" }
      ]
    },

    {
      title: "3D Shapes",
      links: [
        { name: "Volume of Cube", path: "/VolumeCube" },
        { name: "Volume of Cuboid", path: "/VolumeCuboid" },
        { name: "Volume of Cylinder", path: "/VolumeCylinder" },
        { name: "Volume of Cone", path: "/VolumeCone" },
        { name: "Volume of Sphere", path: "/VolumeSphere" },
        { name: "Volume of Pyramid", path: "/VolumePyramid" },
        { name: "Frustum Volume Calculator", path: "/FrustumVolume" },
        { name: "Surface Area of Cube", path: "/SurfaceAreaCube" },
        { name: "Surface Area of Sphere", path: "/SurfaceAreaSphere" },
        { name: "Surface Area of Cone", path: "/SurfaceAreaCone" }
      ]
    },

    {
      title: "Triangles & Angles",
      links: [
        { name: "Triangle Solver", path: "/TriangleSolver" },
        { name: "Angle Bisector Calculator", path: "/AngleBisectorCalculator" },
        { name: "Pythagorean Theorem", path: "/PythagoreanCalculator" },
        { name: "Law of Sines/Cosines", path: "/LawSinesCosines" },
        { name: "Right Triangle Solver", path: "/RightTriangleSolver" },
        { name: "Quadrilateral Calculator", path: "/QuadrilateralCalculator" },
        { name: "Centroid Calculator", path: "/CentroidCalculator" },
        { name: "Incenter Calculator", path: "/IncenterCalculator" },
        { name: "Circumcenter Calculator", path: "/CircumcenterCalculator" },
        { name: "Orthocenter Calculator", path: "/OrthocenterCalculator" },
      ]
    }
  ]
},
    {
  name: "Algebra",
  dropdown: [
    {
      title: "Equations",
      links: [
        { name: "Linear Equation Solver", path: "/LinearEquationSolver" },
        { name: "Quadratic Equation Solver", path: "/QuadraticEquationSolver" },
        { name: "Polynomial Calculator", path: "/PolynomialCalculator" },
        { name: "System of Equations Solver", path: "/SystemEquationsSolver" },
        { name: "Expression Evaluator", path: "/ExpressionEvaluator" },
        { name: "Inequality Calculator", path: "/InequalityCalculator" },
        { name: "Factorization Calculator", path: "/FactorizationCalculator" },
        { name: "Synthetic Division Calculator", path: "/SyntheticDivision" },
        { name: "Complex Roots Finder", path: "/ComplexRootsFinder" },
        { name: "Parabola Calculator", path: "/ParabolaCalculator" }
      ]
    },

    {
      title: "Matrices",
      links: [
        { name: "Matrix Determinant Calculator", path: "/MatrixDeterminant" },
        { name: "Matrix Inverse Calculator", path: "/MatrixInverse" },
        { name: "Matrix Addition/Subtraction", path: "/MatrixAdditionSubtraction" },
        { name: "Matrix Multiplication", path: "/MatrixMultiplication" },
        { name: "Eigenvalue Calculator", path: "/EigenvalueCalculator" },
        { name: "Matrix Rank Calculator", path: "/MatrixRank" },
    { name: "Matrix Transpose Calculator", path: "/MatrixTranspose" },
    { name: "Matrix Trace Calculator", path: "/MatrixTrace" },
    { name: "Adjugate Matrix Calculator", path: "/MatrixAdjugate" },
    { name: "Matrix Power Calculator", path: "/MatrixPower" }
      ]
    },

    {
      title: "Sequences & Series",
      links: [
        { name: "Arithmetic Sequence Calculator", path: "/ArithmeticSequence" },
        { name: "Geometric Sequence Calculator", path: "/GeometricSequence" },
        { name: "Binomial Expansion Calculator", path: "/BinomialExpansion" },
        { name: "Logarithm Calculator", path: "/LogarithmCalculator" },
        { name: "Distance Formula", path: "/DistanceFormula" },
        { name: "Fibonacci Sequence Generator", path: "/FibonacciSequence" },
        { name: "Arithmetic Series Calculator", path: "/ArithmeticSeries" },
    { name: "Geometric Series Calculator", path: "/GeometricSeries" },
    { name: "Infinite Geometric Series", path: "/InfiniteGeometricSeries" },
    { name: "Partial Sum Calculator", path: "/PartialSum" },
      ]
    },

    {
      title: "Complex Numbers",
      links: [
        { name: "Complex Number Calculator", path: "/ComplexNumberCalculator" },
        { name: "Modulus of Complex Number", path: "/ComplexModulus" },
        { name: "Complex Conjugate Calculator", path: "/ComplexConjugate" },
    { name: "Complex Argument Calculator", path: "/ComplexArgument" },
    { name: "Complex Polar Form Converter", path: "/ComplexPolarForm" },
    { name: "Euler's Formula Calculator", path: "/EulersFormula" },
    { name: "Complex Exponential Calculator", path: "/ComplexExponential" },
    { name: "Complex Logarithm Calculator", path: "/ComplexLogarithm" },
    { name: "Complex Addition/Subtraction", path: "/ComplexAddSubtract" },
      ]
    }
  ]
},
    {
  name: "Trigonometry",
  dropdown: [
    {
      title: "Basic Functions",
      links: [
        { name: "Sine Calculator", path: "/SineCalculator" },
        { name: "Cosine Calculator", path: "/CosineCalculator" },
        { name: "Tangent Calculator", path: "/TangentCalculator" },
        { name: "Cot, Sec, Cosec Calculator", path: "/CotSecCosecCalculator" },
        { name: "Inverse Trig Functions", path: "/InverseTrigCalculator" },
        { name: "Trigonometric Identities Calculator", path: "/TrigIdentitiesCalculator" },
        { name: "Unit Circle Calculator", path: "/UnitCircleCalculator" },
        { name: "Reciprocal Trig Calculator", path: "/ReciprocalTrigCalculator" },
        { name: "Trig Values for Special Angles", path: "/TrigValuesSpecialAngles" },
        { name: "Trig Function Simplifier", path: "/TrigFunctionSimplifier" },
      ]
    },

    {
      title: "Solvers",
      links: [
        { name: "SOHCAHTOA Calculator", path: "/SOHCAHTOACalculator" },
        { name: "Law of Sines Calculator", path: "/LawOfSines" },
        { name: "Law of Cosines Calculator", path: "/LawOfCosines" },
        { name: "Angle of Elevation/Depression", path: "/AngleElevationDepression" },
        { name: "Oblique Triangle Solver", path: "/ObliqueTriangleSolver" },
        { name: "SSA Ambiguous Case Solver", path: "/SSAAmbiguousCaseSolver" },
        { name: "Trigonometric Equation Solver", path: "/TrigonometricEquationSolver" },
        { name: "Reference Angle Solver", path: "/ReferenceAngleSolver" },
        { name: "Angle Between Two Lines Calculator", path: "/AngleBetweenTwoLines" },
        { name: "Triangle Classification Calculator", path: "/TriangleClassificationCalculator" },
      ]
    },

    {
      title: "Conversions",
      links: [
        { name: "Degree-Radian Converter", path: "/DegreeRadianConverter" },
        { name: "Degree Minute Second Converter", path: "/DegreeMinuteSecondConverter" },
        { name: "Radian DMS Converter", path: "/RadianDMSConverter" },
        { name: "Gradian Converter", path: "/GradianConverter" },
        { name: "Decimal Degrees Converter", path: "/DecimalDegreesConverter" },
        { name: "Slope to Angle Converter", path: "/SlopeAngleConverter" },
        { name: "Coterminal Angle Calculator", path: "/CoterminalAngleCalculator" },
        { name: "Arc Measure Calculator", path: "/ArcMeasureCalculator" },
        { name: "Circle Sector Calculator", path: "/CircleSectorCalculator" },
        { name: "Rotation Sense Calculator", path: "/RotationSenseCalculator" },
        { name: "Angle Direction Converter", path: "/AngleDirectionConverter" },
        { name: "Bearing Angle Converter", path: "/BearingAngleConverter" },
        { name: "Complement & Supplement", path: "/ComplementSupplementCalculator" },
        { name: "Turn Angle Calculator", path: "/TurnAngleCalculator" },
        { name: "Angle Between Vectors", path: "/AngleBetweenVectors" },
      ]
    }
  ]
},
    {
  name: "Calculus",
  dropdown: [
    {
      title: "Differentiation",
      links: [
        { name: "Derivative Calculator", path: "/DerivativeCalculator" },
        { name: "Partial Derivative Calculator", path: "/PartialDerivativeCalculator" },
        { name: "Derivative Function Plotter", path: "/DerivativeFunctionPlotter" },
        { name: "Limit Calculator", path: "/LimitCalculator" },
        { name: "Mean Value Theorem Calculator", path: "/MeanValueTheoremCalculator" },
        { name: "Higher Order Derivative Calculator", path: "/HigherOrderDerivativeCalculator" },
        { name: "Implicit Differentiation", path: "/ImplicitDifferentiation" },
        { name: "Directional Derivative Calculator", path: "/DirectionalDerivativeCalculator" },
        { name: "Derivative at a Point", path: "/DerivativeAtPoint" },
        { name: "Logarithmic Differentiation", path: "/LogarithmicDifferentiation" }
      ]
    },

    {
      title: "Integration",
      links: [
        { name: "Integral Calculator", path: "/IntegralCalculator" },
        { name: "Area Under Curve", path: "/AreaUnderCurve" },
        { name: "Riemann Sum Calculator", path: "/RiemannSumCalculator" },
        { name: "Double Integral Calculator", path: "/DoubleIntegralCalculator" },
        { name: "Triple Integral Calculator", path: "/TripleIntegralCalculator" },
        { name: "Improper Integral Calculator", path: "/ImproperIntegralCalculator" },
        { name: "Volume of Revolution", path: "/VolumeOfRevolution" },
        { name: "Integration by Parts", path: "/IntegrationByParts" },
        { name: "Substitution Method", path: "/SubstitutionMethod" },
        { name: "Surface Area Integral", path: "/SurfaceAreaIntegral" }
      ]
    },

    {
      title: "Advanced",
      links: [
        { name: "Differential Equation Solver", path: "/DifferentialEquationsSolver" },
        { name: "Taylor Series Calculator", path: "/TaylorSeriesCalculator" },
        { name: "Laplace Transform Calculator", path: "/LaplaceTransformCalculator" },
        { name: "Inverse Laplace Transform", path: "/InverseLaplaceTransform" },
        { name: "Vector Calculus Calculator", path: "/VectorCalculusCalculator" },
        { name: "Fourier Series Calculator", path: "/FourierSeriesCalculator" },
        { name: "Green's Theorem Calculator", path: "/GreensTheoremCalculator" },
        { name: "Stokes' Theorem Calculator", path: "/StokesTheoremCalculator" },
        { name: "Divergence Theorem Calculator", path: "/DivergenceTheoremCalculator" },
        { name: "Complex Analysis Calculator", path: "/ComplexAnalysisCalculator" }
      ]
    }
  ]
},
    {
  name: "Statistics",
  dropdown: [
    {
      title: "Descriptive Stats",
      links: [
        { name: "Mean Calculator", path: "/MeanCalculator" },
        { name: "Median Calculator", path: "/MedianCalculator" },
        { name: "Mode Calculator", path: "/ModeCalculator" },
        { name: "Range Calculator", path: "/RangeCalculator" },
        { name: "Standard Deviation Calculator", path: "/StandardDeviationCalculator" },
        { name: "Variance Calculator", path: "/VarianceCalculator" },
        { name: "Percentile Calculator", path: "/PercentileCalculator" },
        { name: "Quartile Calculator", path: "/QuartileCalculator" },
        { name: "Outlier Detection", path: "/OutlierDetection" },
        { name: "Interquartile Range (IQR) Calculator", path: "/InterquartileRangeCalculator" },
        { name: "Skewness Calculator", path: "/SkewnessCalculator" },
{ name: "Kurtosis Calculator", path: "/KurtosisCalculator" },
{ name: "Five Number Summary", path: "/FiveNumberSummary" },
{ name: "Coefficient of Variation", path: "/CoefficientOfVariation" },
{ name: "Data Frequency Table", path: "/DataFrequencyTable" }

      ]
    },

    {
      title: "Probability",
      links: [
        { name: "Probability Calculator", path: "/ProbabilityCalculator" },
        { name: "Permutation Calculator", path: "/PermutationCalculator" },
        { name: "Combination Calculator", path: "/CombinationCalculator" },
        { name: "Bayes Theorem Calculator", path: "/BayesTheoremCalculator" },
        { name: "Normal Distribution Calculator", path: "/NormalDistributionCalculator" },
        { name: "Binomial Distribution", path: "/BinomialDistribution" },
        { name: "Poisson Distribution", path: "/PoissonDistribution" },
        { name: "Geometric Distribution", path: "/GeometricDistribution" },
{ name: "Hypergeometric Distribution", path: "/HypergeometricDistribution" },
{ name: "Exponential Distribution", path: "/ExponentialDistribution" },
{ name: "Discrete Probability Distribution", path: "/DiscreteProbabilityDistribution" },
{ name: "Continuous Probability Distribution", path: "/ContinuousProbabilityDistribution" },
{ name: "Joint Probability Calculator", path: "/JointProbabilityCalculator" },
{ name: "Conditional Probability", path: "/ConditionalProbability" },
{ name: "Random Variable Calculator", path: "/RandomVariableCalculator" }
      ]
    },

    {
      title: "Statistical Tests",
      links: [
        { name: "Z-Score Calculator", path: "/ZScoreCalculator" },
        { name: "p-value Calculator", path: "/PValueCalculator" },
        { name: "T-test Calculator", path: "/TTestCalculator" },
        { name: "Chi-square Calculator", path: "/ChiSquareCalculator" },
        { name: "Correlation Coefficient", path: "/CorrelationCoefficient" },
        { name: "Regression Calculator", path: "/RegressionCalculator" },
        { name: "ANOVA Calculator", path: "/AnovaCalculator" },
{ name: "Confidence Interval Calculator", path: "/ConfidenceInterval" },
{ name: "F-Test Calculator", path: "/FTestCalculator" },
{ name: "Covariance Calculator", path: "/CovarianceCalculator" },
{ name: "Paired T-Test Calculator", path: "/PairedTTestCalculator" },
{ name: "One Sample T-Test", path: "/OneSampleTTest" },
{ name: "Two Sample T-Test", path: "/TwoSampleTTest" },
{ name: "Spearman Rank Correlation", path: "/SpearmanCorrelationCalculator" },
{ name: "Pearson Correlation Test", path: "/PearsonCorrelation" }
      ]
    }
  ]
},
    {
  name: "Financial",
  dropdown: [
    {
      title: "Time Value of Money",
      links: [
        { name: "Present Value Calculator", path: "/PresentValueCalculator" },
        { name: "Future Value Calculator", path: "/FutureValueCalculator" },
        { name: "Simple Interest Formula", path: "/SimpleInterestCalculator" },
        { name: "Compound Interest Formula", path: "/CompoundInterestCalculator" },
        { name: "Continuous Compounding Calculator", path: "/ContinuousCompoundingCalculator" },
        { name: "Discount Factor Calculator", path: "/DiscountFactorCalculator" },
        { name: "Accumulation Factor Calculator", path: "/AccumulationFactorCalculator" },
        { name: "Effective Interest Rate Calculator", path: "/EffectiveInterestRateCalculator" },
        { name: "Nominal Interest Rate Calculator", path: "/NominalInterestRateCalculator" },
        { name: "Interest Conversion Calculator", path: "/InterestConversionCalculator" },
        { name: "Force of Interest Calculator", path: "/ForceOfInterestCalculator" },
  { name: "Equivalent Interest Rate Calculator", path: "/EquivalentInterestRateCalculator" },
  { name: "Accumulated Value Calculator", path: "/AccumulatedValueCalculator" },
  { name: "Discount Rate Conversion Calculator", path: "/DiscountRateConversion" },
  { name: "Time Period Calculator (Interest)", path: "/InterestTimePeriodCalculator" }
      ]
    },

    {
      title: "Annuities & Cash Flows",
      links: [
        { name: "Ordinary Annuity Calculator", path: "/OrdinaryAnnuityCalculator" },
        { name: "Annuity Due Calculator", path: "/AnnuityDueCalculator" },
        { name: "Perpetuity Calculator", path: "/PerpetuityCalculator" },
        { name: "Cash Flow Present Value", path: "/CashFlowPresentValue" },
        { name: "Cash Flow Future Value", path: "/CashFlowFutureValue" },
        { name: "Growing Annuity Calculator", path: "/GrowingAnnuityCalculator" },
        { name: "Deferred Annuity Calculator", path: "/DeferredAnnuityCalculator" },
        { name: "Annuity Factor Calculator", path: "/AnnuityFactorCalculator" },
        { name: "Sinking Fund Calculator", path: "/SinkingFundCalculator" },
        { name: "Capital Recovery Factor", path: "/CapitalRecoveryFactor" },
        { name: "Annuity Payment Calculator", path: "/AnnuityPaymentCalculator" },
  { name: "Outstanding Loan Balance Calculator", path: "/OutstandingLoanBalanceCalculator" },
  { name: "Amortization Factor Calculator", path: "/AmortizationFactorCalculator" },
  { name: "Gradient Annuity Calculator", path: "/GradientAnnuityCalculator" },
  { name: "Equivalent Annual Worth Calculator", path: "/EquivalentAnnualWorthCalculator" }
      ]
    },

    {
      title: "Financial Rates & Valuation",
      links: [
        { name: "Net Present Value (NPV)", path: "/NetPresentValueCalculator" },
        { name: "Internal Rate of Return (IRR)", path: "/InternalRateOfReturnCalculator" },
        { name: "Modified IRR (MIRR)", path: "/ModifiedIRRCalculator" },
        { name: "Discount Rate Calculator", path: "/DiscountRateCalculator" },
        { name: "Yield to Maturity (YTM)", path: "/YieldToMaturityCalculator" },
        { name: "Bond Price Calculator", path: "/BondPriceCalculator" },
        { name: "Bond Duration Calculator", path: "/BondDurationCalculator" },
        { name: "Bond Convexity Calculator", path: "/BondConvexityCalculator" },
        { name: "Payback Period Calculator", path: "/PaybackPeriodCalculator" },
        { name: "Profitability Index", path: "/ProfitabilityIndexCalculator" },
        { name: "Holding Period Return Calculator", path: "/HoldingPeriodReturnCalculator" },
  { name: "Spot Rate Calculator", path: "/SpotRateCalculator" },
  { name: "Forward Rate Calculator", path: "/ForwardRateCalculator" },
  { name: "Zero-Coupon Bond Calculator", path: "/ZeroCouponBondCalculator" },
  { name: "Capital Asset Pricing Model (CAPM)", path: "/CapitalAssetPricingModelCalculator" }
      ]
    }
  ]
},
    {
  name: "Converters",
  dropdown: [
    {
      title: "Unit Converters",
      links: [
        { name: "Length Converter", path: "/LengthConverter" },
        { name: "Weight Converter", path: "/WeightConverter" },
        { name: "Temperature Converter", path: "/TemperatureConverter" },
        { name: "Area Converter", path: "/AreaConverter" },
        { name: "Volume Converter", path: "/VolumeConverter" },
        { name: "Speed Converter", path: "/SpeedConverter" },
        { name: "Time Converter", path: "/TimeConverter" },
        { name: "Pressure Converter", path: "/PressureConverter" },
        { name: "Energy Converter", path: "/EnergyConverter" },
        { name: "Power Converter", path: "/PowerConverter" },
        { name: "Force Converter", path: "/ForceConverter" },
        { name: "Density Converter", path: "/DensityConverter" },
        { name: "Angle Converter", path: "/AngleConverter" },
        { name: "Frequency Converter", path: "/FrequencyConverter" },
        { name: "Torque Converter", path: "/TorqueConverter" },
        { name: "Fuel Efficiency Converter", path: "/FuelEfficiencyConverter" },
        { name: "Sound Level Converter", path: "/SoundLevelConverter" },
        { name: "Illumination Converter", path: "/IlluminationConverter" },
        { name: "Magnetic Field Converter", path: "/MagneticFieldConverter" },
        { name: "Viscosity Converter", path: "/ViscosityConverter" }
      ]
    },

    {
      title: "Digital & Number",
      links: [
        { name: "Data Storage Converter", path: "/DataStorageConverter" },
        { name: "Base Number Converter", path: "/BaseNumberConverter" },
        { name: "Binary Calculator", path: "/BinaryCalculator" },
        { name: "Hexadecimal Calculator", path: "/HexadecimalCalculator" },
        { name: "Roman Numeral Converter", path: "/RomanNumeralConverter" },
        { name: "Octal Calculator", path: "/OctalCalculator" },
        { name: "Decimal to Binary Converter", path: "/DecimalToBinaryConverter" },
        { name: "Decimal to Hexadecimal Converter", path: "/DecimalToHexadecimalConverter" },
        { name: "Binary to Decimal Converter", path: "/BinaryToDecimalConverter" },
        { name: "Hexadecimal to Decimal Converter", path: "/HexadecimalToDecimalConverter" },
        { name: "Binary to Hexadecimal Converter", path: "/BinaryToHexadecimalConverter" },
        { name: "Hexadecimal to Binary Converter", path: "/HexadecimalToBinaryConverter" },
        { name: "Binary to Octal Converter", path: "/BinaryToOctalConverter" },
        { name: "Octal to Binary Converter", path: "/OctalToBinaryConverter" },
        { name: "Octal to Decimal Converter", path: "/OctalToDecimalConverter" },
        { name: "Decimal to Octal Converter", path: "/DecimalToOctalConverter" },
        { name: "Number System Calculator", path: "/NumberSystemCalculator" },
        { name: "Bitwise Calculator", path: "/BitwiseCalculator" },
        { name: "ASCII Converter", path: "/ASCIIConverter" },
        { name: "Unicode Converter", path: "/UnicodeConverter" }
      ]
    }
  ]
},
    {
  name: "Advanced Math",
  dropdown: [
    {
      title: "Special Functions",
      links: [
        { name: "Fibonacci Calculator", path: "/FibonacciCalculator" },
        { name: "Factorial Calculator", path: "/FactorialCalculator" },
        { name: "Prime Counting Function", path: "/PrimeCountingFunction" },
        { name: "Prime Factorization", path: "/PrimeFactorizationCalculator" },
        { name: "Modular Arithmetic Calculator", path: "/ModularArithmeticCalculator" },
        { name: "Continued Fraction Calculator", path: "/ContinuedFractionCalculator" },
        { name: "Galois Field Calculator", path: "/GaloisFieldCalculator" },
        { name: "Collatz Sequence Calculator", path: "/CollatzSequenceCalculator" },
        { name: "Sequence Pattern Calculator", path: "/SequencePatternCalculator" },
        { name: "Number Base Pattern Detector", path: "/NumberBasePatternDetector" },
        { name: "Lucas Number Calculator", path: "/LucasNumbersCalculator" },
{ name: "Bernoulli Numbers Calculator", path: "/BernoulliNumbersCalculator" },
{ name: "Euler Totient Function", path: "/EulerTotientFunction" },
{ name: "Partition Function Calculator", path: "/PartitionFunctionCalculator" },
{ name: "Mobius Function Calculator", path: "/MobiusFunctionCalculator" }
      ]
    },

    {
      title: "Advanced Tools",
      links: [
        { name: "Fourier Transform Calculator", path: "/FourierTransformCalculator" },
        { name: "Probability Density Function Viewer", path: "/ProbabilityDensityFunctionViewer" },
        { name: "Combinatorial Sum Calculator", path: "/CombinatorialSumCalculator" },
        { name: "Random Matrix Generator", path: "/RandomMatrixGenerator" },
        { name: "Linear Programming Solver", path: "/LinearProgrammingSolver" },
        { name: "Optimization Calculator", path: "/OptimizationCalculator" },
        { name: "Step Function Calculator", path: "/StepFunctionCalculator" },
        { name: "Hash Calculator (MD5, SHA1, SHA256)", path: "/HashCalculator" },
        { name: "Z-Transform Calculator", path: "/ZTransformCalculator" },
{ name: "Inverse Z-Transform", path: "/InverseZTransformCalculator" },
{ name: "Wavelet Transform Calculator", path: "/WaveletTransformCalculator" },
{ name: "Discrete Cosine Transform (DCT)", path: "/DiscreteCosineTransformCalculator" },
{ name: "Singular Value Decomposition (SVD)", path: "/SingularValueDecompositionCalculator" },
{ name: "Markov Chain Calculator", path: "/MarkovChainCalculator" },
{ name: "Numerical Integration Calculator", path: "/NumericalIntegrationCalculator" }
      ]
    },

    {
      title: "Regression & Graphing",
      links: [
        { name: "Graph Plotter", path: "/GraphPlotter" },
        { name: "Logarithmic Regression", path: "/LogarithmicRegressionCalculator" },
        { name: "Exponential Regression", path: "/ExponentialRegressionCalculator" },
        { name: "Linear Regression Calculator", path: "/LinearRegressionCalculator" },
{ name: "Polynomial Regression", path: "/PolynomialRegressionCalculator" },
{ name: "Multiple Regression Calculator", path: "/MultipleRegressionCalculator" },
{ name: "Logistic Regression", path: "/LogisticRegressionCalculator" },
{ name: "Power Regression", path: "/PowerRegressionCalculator" },
{ name: "Piecewise Function Plotter", path: "/PiecewiseFunctionPlotter" },
{ name: "Parametric Graph Plotter", path: "/ParametricGraphPlotter" },
{ name: "Polar Graph Plotter", path: "/PolarGraphPlotter" },
{ name: "3D Surface Plotter", path: "/SurfacePlotter" },
{ name: "Residual Plot Generator", path: "/ResidualPlotGenerator" },
{ name: "Best Fit Curve Finder", path: "/BestFitCurveFinder" },
{ name: "Scatter Plot Generator", path: "/ScatterPlotGenerator" }
      ]
    }
  ]
},
  ];

  return (
    <header className="navigation" ref={navRef} role="navigation" aria-label="Main navigation">
      <div className="nav-container">
        <div className="mobile-nav-header">
  <NavLink 
    to="/" 
    className="mobile-logo" 
    style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px' }}
  >
    <span>D-Tech</span>
    <div className="parallelogram">
      <span>Studios</span>
    </div>
  </NavLink>
</div>

        {/* Mobile menu button with improved accessibility */}
        <button 
          className={`nav-mobile ${mobileOpen ? "active" : ""}`}
          onClick={toggleMobile}
          aria-expanded={mobileOpen}
          aria-label="Toggle navigation menu"
          aria-controls="mobile-nav"
        >
          <span></span>
          <span></span>
          <span></span>
          <span className="sr-only">Menu</span>
        </button>

        {/* Enhanced Mobile Navigation */}
        <nav 
          id="mobile-nav"
          className={`mobile-nav ${mobileOpen ? "open" : ""}`}
          aria-label="Mobile navigation"
        >
          <div className="mobile-nav-header">
            <div className="mobile-logo">
              <span>D-Tech</span>
              <div className="parallelogram">
                <span>Studios</span>
              </div>
            </div>
            <button 
              className="mobile-close-btn"
              onClick={closeMobileMenu}
              aria-label="Close menu"
            >
              ×
            </button>
          </div>

          <div className="mobile-nav-content">
            {mobileSubMenu === null ? (
              <ul className="mobile-menu" role="menu">
                {menuItems.map((item, idx) => (
                  <li key={idx} className="mobile-nav-item" role="none">
                    {item.dropdown ? (
                      <button
                        className="mobile-nav-link mobile-dropdown-btn"
                        onClick={() => openMobileSubMenu(idx)}
                        onKeyDown={(e) => handleMobileKeyDown(e, idx)}
                        role="menuitem"
                        aria-expanded="false"
                      >
                        <span>{item.name}</span>
                        <span className="mobile-arrow" aria-hidden="true">▶</span>
                      </button>
                    ) : (
                      <NavLink 
                        to={item.path} 
                        className="mobile-nav-link"
                        onClick={closeMobileMenu}
                        role="menuitem"
                        tabIndex={mobileOpen ? 0 : -1}
                      >
                        {item.name}
                      </NavLink>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mobile-submenu" role="menu">
                <button 
                  className="mobile-back-btn" 
                  onClick={closeMobileSubMenu}
                  role="menuitem"
                  tabIndex={mobileOpen ? 0 : -1}
                >
                  <span className="back-arrow" aria-hidden="true">←</span>
                  Back to Menu
                </button>
                <div className="mobile-submenu-content">
                  {menuItems[mobileSubMenu].dropdown.map((section, sIdx) => (
                    <div key={sIdx} className="mobile-submenu-section">
                      <h4 className="mobile-submenu-title">{section.title}</h4>
                      <div className="mobile-submenu-links">
                        {section.links.map((link, lIdx) => (
                          <NavLink
                            key={lIdx}
                            to={link.path}
                            className={`mobile-submenu-link ${location.pathname === link.path ? 'active' : ''}`}
                            onClick={closeMobileMenu}
                            role="menuitem"
                            tabIndex={mobileOpen ? 0 : -1}
                          >
                            {link.name}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Mobile Nav Footer */}
          <div className="mobile-nav-footer">
            <div className="nav-glow"></div>
            <p>Math Tools & Calculators</p>
          </div>
        </nav>

        {/* Overlay for mobile menu */}
        <div 
          className={`mobile-overlay ${mobileOpen ? "active" : ""}`}
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      </div>
    </header>
  );
};

export default NavBar;